/**
 * Captura de voz do composer do Chat IA — Ditar e Modo de voz (v1, por
 * turnos: grava um Blob por gesto, sem streaming).
 *
 * Espelha o padrão de
 * `fee-fixo/components/fee-fixo-roleplay-fluido/midia-do-navegador.js`: todo
 * acesso a `globalThis`/`navigator` fica concentrado aqui, pra ser
 * substituível por dublê no Jest — o jsdom não implementa `MediaRecorder`,
 * `AudioContext` nem `navigator.mediaDevices.getUserMedia`. O resto do
 * composer (`send-message.vue`) fica puro em cima desta camada.
 *
 * A classe de erro é duplicada (não importada de
 * `fee-fixo/utils/roleplay-gravador.js`) de propósito: importar dali
 * acoplaria o motor de chat a um módulo de produto.
 */

export const MOTIVO_SEM_SUPORTE = 'sem_suporte'
export const MOTIVO_PERMISSAO_NEGADA = 'permissao_negada'
export const MOTIVO_SEM_MICROFONE = 'sem_microfone'
export const MOTIVO_FALHA = 'falha_ao_gravar'

// Os dois modos de gravação do composer (ver `send-message.vue`). Moram aqui
// (e não em `composer-mic-menu.vue`, que existiu e foi removido no pivô pro
// desenho estilo ChatGPT) por serem o módulo mais neutro — nem componente de
// UI, nem específico de uma tela.
export const MODO_DITAR = 'ditar'
export const MODO_VOZ = 'voz'

const MENSAGEM_POR_MOTIVO = {
  [MOTIVO_SEM_SUPORTE]: 'Este navegador não grava áudio.',
  [MOTIVO_PERMISSAO_NEGADA]: 'O acesso ao microfone foi negado.',
  [MOTIVO_SEM_MICROFONE]: 'Nenhum microfone foi encontrado.',
  [MOTIVO_FALHA]: 'Não foi possível gravar áudio agora.',
}

export class GravacaoDeVozIndisponivelError extends Error {
  constructor(motivo) {
    super(MENSAGEM_POR_MOTIVO[motivo] || MENSAGEM_POR_MOTIVO[MOTIVO_FALHA])
    this.name = 'GravacaoDeVozIndisponivelError'
    this.motivo = motivo || MOTIVO_FALHA
  }
}

/**
 * `NotAllowedError`/`SecurityError` é permissão negada; `NotFoundError`/
 * `DevicesNotFoundError` é ausência de hardware. Qualquer outro nome cai em
 * "falha ao gravar" — motivo genérico, mas nunca silencioso.
 */
export const motivoDoErroDeMidia = (erro) => {
  const nome = (erro && erro.name) || ''
  if (nome === 'NotAllowedError' || nome === 'SecurityError') {
    return MOTIVO_PERMISSAO_NEGADA
  }
  if (nome === 'NotFoundError' || nome === 'DevicesNotFoundError') {
    return MOTIVO_SEM_MICROFONE
  }
  return MOTIVO_FALHA
}

/**
 * O navegador tem o mínimo pra gravar e medir o microfone?
 *
 * `getUserMedia` (captura), `MediaRecorder` (grava o turno num Blob) e
 * `AudioContext`/`webkitAudioContext` (mede o nível pras barras). Permissão
 * de microfone não entra aqui — perguntar abriria o prompt do navegador antes
 * de o usuário sequer tocar no mic; a recusa é tratada em
 * `pedirMicrofonePadrao`.
 *
 * @returns {boolean}
 */
export const suportaEntradaDeVoz = () => {
  const midia = globalThis.navigator && globalThis.navigator.mediaDevices
  const temMicrofone = !!(midia && typeof midia.getUserMedia === 'function')
  const temGravador = typeof globalThis.MediaRecorder === 'function'
  const temWebAudio = !!(
    globalThis.AudioContext || globalThis.webkitAudioContext
  )
  return temMicrofone && temGravador && temWebAudio
}

/**
 * @param {MediaStreamConstraints} [constraints]
 * @returns {Promise<MediaStream>}
 */
export const pedirMicrofonePadrao = (constraints) => {
  const midia = globalThis.navigator && globalThis.navigator.mediaDevices
  if (!midia || typeof midia.getUserMedia !== 'function') {
    return Promise.reject(
      new GravacaoDeVozIndisponivelError(MOTIVO_SEM_SUPORTE)
    )
  }
  return midia.getUserMedia(constraints || { audio: true }).catch((erro) => {
    throw new GravacaoDeVozIndisponivelError(motivoDoErroDeMidia(erro))
  })
}

/**
 * [V1, voz v3, 08/09] O pedido de mic SEM `echoCancellation`/`noiseSuppression`
 * era um dos dois fatores por trás do STT alucinando em cima de silêncio: sem
 * cancelamento de eco, a fala da própria IA (tocando pelo alto-falante) e o
 * ruído de fundo vazavam de volta pro microfone e viravam "fala" para o VAD
 * local — e é justamente esse vazamento que o Modo de Voz precisa suprimir
 * pra o barge-in (`motor-de-voz.js`) não se auto-disparar com o próprio áudio
 * da IA.
 */
export const CONSTRAINTS_DE_MICROFONE = {
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
  },
}

/**
 * Pede o microfone com as constraints RICAS acima. Cai para `{audio: true}`
 * só quando o navegador RECUSA o objeto de constraints em si — o catch-all
 * `MOTIVO_FALHA` de `motivoDoErroDeMidia` (inclui `OverconstrainedError`, de
 * navegadores/dispositivos antigos que não suportam as três flags juntas).
 * Permissão negada (`MOTIVO_PERMISSAO_NEGADA`) ou ausência de hardware
 * (`MOTIVO_SEM_MICROFONE`) NÃO mudam com constraints mais simples — propagam
 * direto, sem gastar um segundo round-trip (e um 2º prompt do navegador).
 *
 * @returns {Promise<MediaStream>}
 */
export const pedirMicrofoneComFallback = async () => {
  try {
    return await pedirMicrofonePadrao(CONSTRAINTS_DE_MICROFONE)
  } catch (erro) {
    if (
      erro instanceof GravacaoDeVozIndisponivelError &&
      erro.motivo !== MOTIVO_FALHA
    ) {
      throw erro
    }
    return pedirMicrofonePadrao({ audio: true })
  }
}

// Testado primeiro no Chrome/Edge/Firefox; `audio/mp4` é o fallback do
// Safari, que não suporta `opus` em `MediaRecorder`.
const MIMES_PREFERIDOS = ['audio/webm;codecs=opus', 'audio/mp4']

const escolherMime = () => {
  const Gravador = globalThis.MediaRecorder
  if (!Gravador || typeof Gravador.isTypeSupported !== 'function') return ''
  return MIMES_PREFERIDOS.find((mime) => Gravador.isTypeSupported(mime)) || ''
}

/**
 * `MediaRecorder` de um turno só — um `Blob` por gesto de iniciar/parar.
 *
 * @param {MediaStream} stream
 * @returns {{iniciar: Function, parar: () => Promise<{blob: Blob, contentType: string, duracaoSegundos: number}>}}
 */
export const criarGravador = (stream) => {
  const Gravador = globalThis.MediaRecorder
  if (!Gravador) throw new GravacaoDeVozIndisponivelError(MOTIVO_SEM_SUPORTE)

  const mime = escolherMime()
  const gravador = mime
    ? new Gravador(stream, { mimeType: mime })
    : new Gravador(stream)
  const chunks = []
  let inicio = 0

  gravador.addEventListener('dataavailable', (evento) => {
    if (evento && evento.data && evento.data.size) chunks.push(evento.data)
  })

  return {
    iniciar() {
      inicio = Date.now()
      gravador.start()
    },
    parar() {
      return new Promise((resolve) => {
        gravador.addEventListener('stop', () => {
          const contentType = mime || gravador.mimeType || 'audio/webm'
          resolve({
            blob: new Blob(chunks, { type: contentType }),
            contentType,
            duracaoSegundos: (Date.now() - inicio) / 1000,
          })
        })
        gravador.stop()
      })
    },
  }
}

/**
 * `AnalyserNode` NUNCA conectado a `destination` — só mede, nunca reproduz o
 * próprio microfone de volta. `iniciar(aoNivel)` chama `aoNivel(rms)` a cada
 * frame via `requestAnimationFrame`, sem auto-stop por silêncio: o Modo de
 * Voz não re-escuta sozinho, e é o usuário/botão quem decide parar.
 *
 * @param {MediaStream} stream
 * @returns {{iniciar: (aoNivel: (rms: number) => void) => void, parar: () => void}}
 */
export const criarMedidorDeNivel = (stream) => {
  const Construtor = globalThis.AudioContext || globalThis.webkitAudioContext
  if (!Construtor) throw new GravacaoDeVozIndisponivelError(MOTIVO_SEM_SUPORTE)

  const contexto = new Construtor()
  const fonte = contexto.createMediaStreamSource(stream)
  const analisador = contexto.createAnalyser()
  analisador.fftSize = 512
  fonte.connect(analisador)

  const buffer = new Float32Array(analisador.fftSize)
  let quadroAtivo = null

  const medir = (aoNivel) => {
    analisador.getFloatTimeDomainData(buffer)
    let somaQuadrados = 0
    for (const amostra of buffer) somaQuadrados += amostra * amostra
    aoNivel(Math.sqrt(somaQuadrados / buffer.length))
    if (typeof globalThis.requestAnimationFrame === 'function') {
      quadroAtivo = globalThis.requestAnimationFrame(() => medir(aoNivel))
    }
  }

  return {
    iniciar(aoNivel) {
      if (typeof aoNivel === 'function') medir(aoNivel)
    },
    parar() {
      if (quadroAtivo !== null && typeof globalThis.cancelAnimationFrame === 'function') {
        globalThis.cancelAnimationFrame(quadroAtivo)
      }
      quadroAtivo = null
      if (typeof fonte.disconnect === 'function') fonte.disconnect()
      if (typeof contexto.close === 'function') contexto.close()
    },
  }
}

/** Para todas as tracks do stream — desliga o LED do microfone. */
export const liberarStream = (stream) => {
  if (!stream || typeof stream.getTracks !== 'function') return
  stream.getTracks().forEach((track) => {
    if (track && typeof track.stop === 'function') track.stop()
  })
}

/**
 * Compõe mic + gravador + medidor numa API única por turno.
 *
 * `iniciar({aoNivel})` pede o microfone e arranca gravação + medidor;
 * `parar()` desliga tudo (incluindo o stream) e devolve o resultado do
 * gravador; `blob()` expõe o último resultado depois de `parar()`.
 */
export class GravadorDeTurno {
  constructor() {
    this._stream = null
    this._gravador = null
    this._medidor = null
    this._resultado = null
  }

  /**
   * @param {Object} [opts]
   * @param {(rms: number) => void} [opts.aoNivel]
   */
  async iniciar({ aoNivel } = {}) {
    this._stream = await pedirMicrofoneComFallback()
    this._gravador = criarGravador(this._stream)
    this._gravador.iniciar()
    this._medidor = criarMedidorDeNivel(this._stream)
    this._medidor.iniciar(aoNivel)
  }

  async parar() {
    if (this._medidor) this._medidor.parar()
    this._resultado = this._gravador ? await this._gravador.parar() : null
    liberarStream(this._stream)
    this._stream = null
    return this._resultado
  }

  blob() {
    return this._resultado
  }

  /**
   * [feedback do usuário, 04/09] Botão de mudo do Modo de Voz — "para
   * conseguir mutar por exemplo". `track.enabled = false` silencia o áudio
   * SEM parar a gravação nem soltar o microfone: o `MediaRecorder` continua
   * rodando (grava silêncio nesse trecho) e o medidor de nível acompanha —
   * as barras do espectro naturalmente encolhem, porque a amostra vira zero.
   * Diferente de `parar()`: aqui a sessão de voz continua viva.
   */
  definirMudo(mudo) {
    if (!this._stream || typeof this._stream.getAudioTracks !== 'function') {
      return
    }
    this._stream.getAudioTracks().forEach((track) => {
      track.enabled = !mudo
    })
  }
}
